import { useCallback, useEffect, useRef, useState } from 'react';
import Icon from 'shared/Icon';
import { Flex, SxStyleProp, Text } from 'theme-ui';
import Tooltip, { placementsList } from '@picsart/rc/old/Tooltip';
import useClickAway from 'react-use/lib/useClickAway';
import useToggle from 'react-use/lib/useToggle';
import { useDispatch, useSelector } from 'react-redux';
import { getScenes } from 'modules/scenes';
import { SceneType } from 'types/Scene';
import { toggleShortcutsPopup } from 'modules/shortcutsPopup';
import { useLanguageByUser, useTranslator } from '@picsart/rc/services/localization';
import LanguageSwitcher from '@picsart/rc/old/LanguageSwitcher';

const helpWidgetWraper: SxStyleProp = {
  position: 'absolute',
  right: 16,
  bottom: 140,
  zIndex: 'modal'
};

const helpWidgetWrapperOpened: SxStyleProp = {
  ...helpWidgetWraper,
  bottom: 24
};

const helpWidgetContainer: SxStyleProp = {
  width: 56,
  height: 56,
  borderRadius: '50%',
  bg: 'white',
  margin: 'auto',
  justifyContent: 'center',
  boxShadow: 'default'
};

const menuContainer: SxStyleProp = {
  width: 180,
  bg: 'white',
  borderRadius: 'modal',
  padding: '20px',
  position: 'absolute',
  bottom: 90,
  right: 0,
  boxShadow: '0px 12px 34px rgba(87, 88, 118, 0.25)',
  '&::after': {
    content: '""',
    width: 0,
    height: 0,
    position: 'absolute',
    bottom: '-12px',
    right: 20,
    borderLeft: '10px solid transparent',
    borderRight: '10px solid transparent',
    borderTop: '12px solid #fff'
  }
};

const helpTextStyle = {
  fontSize: 2,
  mb: 1,
  cursor: 'pointer',
  ':hover': { fontWeight: 500 }
};

export const HelpWidget = ({ isScenesPanelOpened }: { isScenesPanelOpened: boolean }) => {
  const [on, toggle] = useToggle(false);
  const ref = useRef(null);
  const scenes = useSelector(getScenes);
  const [showLanguageSwitcher, setShowLanguageSwitcher] = useState(false);
  const { currentLanguage } = useLanguageByUser();

  const translator = useTranslator();

  const handleClickChangeLanguage = useCallback(() => {
    showLanguageSwitcher && setShowLanguageSwitcher(false);
  }, [showLanguageSwitcher, setShowLanguageSwitcher]);

  useClickAway(ref, (e: any) => {
    if (e.target?.id === 'helpWidget' || e.target?.parent?.id === 'helpWidget') return;
    window.zE('webWidget', 'close');
    toggle(false);
  });
  useEffect(() => {
    if (!window.zE) return;
    window.zE('webWidget', 'updateSettings', {
      webWidget: {
        chat: {
          departments: {
            enabled: []
          }
        },
        color: { theme: '#C209C1' },
        position: { horizontal: 'right', vertical: 'bottom' },
        helpCenter: {
          messageButton: {
            '*': 'Contact Support'
          }
        }
      }
    });
    window.zE('webWidget:on', 'close', () => {
      toggle(false);
      window.zE('webWidget', 'hide');
    });
  }, []);

  const openHelpCenterHandler = useCallback(() => {
    if (!window.zE) return;
    window.zE('webWidget', 'setLocale', currentLanguage);
    setTimeout(() => {
      if (!window.zE) return;
      window.zE('webWidget', 'helpCenter:setSuggestions', { labels: ['goldwidget'] });
      window.zE('webWidget', 'show');
      window.zE('webWidget', 'open');
    }, 300);
  }, [currentLanguage]);

  const dispatch = useDispatch();
  const openShortcutsPopup = useCallback(() => {
    dispatch(toggleShortcutsPopup(true));
  }, []);

  return (
    <div
      sx={{
        '.language-switcher': {
          position: 'absolute',
          top: '14px',
          right: '14px',
          zIndex: 'popup'
        }
      }}
    >
      <LanguageSwitcher
        show={showLanguageSwitcher}
        onChooseLanguage={handleClickChangeLanguage}
        className="language-switcher"
      />
      <div
        sx={
          isScenesPanelOpened || !scenes.some(scene => scene.type !== SceneType.EMPTY)
            ? helpWidgetWrapperOpened
            : helpWidgetWraper
        }
      >
        {on && (
          <div ref={ref} sx={menuContainer}>
            <Text variant="body" sx={helpTextStyle} onClick={() => setShowLanguageSwitcher(true)}>
              {translator('Language settings')}
            </Text>
            <Text variant="body" sx={helpTextStyle} onClick={() => openShortcutsPopup()}>
              {translator('Keyboard_shortcuts')}
            </Text>
            <a
              rel="noopener noreferrer"
              target="_blank"
              href="https://docs.google.com/forms/d/e/1FAIpQLSd5FSVuJsm6EZ1wVyaJ2O6ObKdh7nxTvD7eGHhcEqXOMXLS8g/viewform"
            >
              <Text variant="body" sx={helpTextStyle}>
                {translator('Submit_Feedback')}
              </Text>
            </a>
            <Text variant="body" sx={{ ...helpTextStyle, mb: 0 }} onClick={openHelpCenterHandler}>
              {translator('Get_Help')}
            </Text>
          </div>
        )}
        <Tooltip content={translator('Help')} placement={placementsList.top}>
          <Flex id="helpWidget" sx={helpWidgetContainer} onClick={() => toggle(!on)}>
            <Icon name="help-editor" sx={{ width: '28px', height: '100%', display: 'flex' }} />
          </Flex>
        </Tooltip>
      </div>
    </div>
  );
};
